/**
 * Utility functions for handling notification preference conversions
 */

// Notification preference enum values
export const NOTIFICATION_PREFS = {
  BOTH: 0,
  EMAIL_ONLY: 1,
  TEXT_ONLY: 2,
  UNSUBSCRIBE: 3,
};

/**
 * Convert notification preference enum to individual toggles
 * @param {number|string} pref - The notification preference value
 * @returns {Object} Object with receiveEmailNotifications and receiveTextNotifications booleans
 */
export const getNotificationMethodsFromPref = pref => {
  const prefString = pref ? pref.toString() : '0';
  return {
    receiveEmailNotifications: ['0', '1'].includes(prefString), // both or email
    receiveTextNotifications: ['0', '2'].includes(prefString), // both or text_message
  };
};

/**
 * Convert individual toggles back to notification preference enum
 * @param {boolean} receiveEmail - Whether to receive email notifications
 * @param {boolean} receiveText - Whether to receive text notifications
 * @returns {number} The notification preference enum value
 */
export const getNotificationPrefFromToggles = (receiveEmail, receiveText) => {
  if (receiveEmail && receiveText) return NOTIFICATION_PREFS.BOTH;
  if (receiveEmail && !receiveText) return NOTIFICATION_PREFS.EMAIL_ONLY;
  if (!receiveEmail && receiveText) return NOTIFICATION_PREFS.TEXT_ONLY;
  return NOTIFICATION_PREFS.UNSUBSCRIBE;
};

/**
 * Get default notification form values
 * @param {Object} accountSettings - The account settings data
 * @returns {Object} Default form values
 */
export const getDefaultNotificationValues = (accountSettings = {}) => {
  const notificationMethods = getNotificationMethodsFromPref(
    accountSettings.notificationPreference,
  );

  return {
    receiveEmailNotifications: notificationMethods.receiveEmailNotifications,
    receiveTextNotifications: notificationMethods.receiveTextNotifications,
    sendWeeklyActivityReport: accountSettings.sendWeeklyActivityReport ?? true,
    receivesExploreEmail: accountSettings.receivesExploreEmail ?? true,
    receivesCampaignFeedbackRecap:
      accountSettings.receivesCampaignFeedbackRecap ?? true,
    receivesPendingQuestionsNotifications:
      accountSettings.receivesPendingQuestionsNotifications ?? true,
  };
};

/**
 * Check if notification form values have changed from original settings
 * @param {Object} formValues - Current form values
 * @param {Object} accountSettings - Original account settings
 * @returns {boolean} Whether the form has changes
 */
export const hasNotificationChanges = (formValues, accountSettings = {}) => {
  const originalValues = getDefaultNotificationValues(accountSettings);

  return Object.keys(formValues).some(
    key => formValues[key] !== originalValues[key],
  );
};

/**
 * Convert form values to API payload format (snake_case)
 * @param {Object} formValues - The form values to convert
 * @returns {Object} API payload with snake_case keys
 */
export const createNotificationPayload = formValues => {
  const notificationPrefCd = getNotificationPrefFromToggles(
    formValues.receiveEmailNotifications,
    formValues.receiveTextNotifications,
  );

  return {
    notification_pref_cd: notificationPrefCd,
    send_weekly_activity_report: formValues.sendWeeklyActivityReport,
    receives_explore_email: formValues.receivesExploreEmail,
    receives_campaign_feedback_recap: formValues.receivesCampaignFeedbackRecap,
    receives_pending_questions_notifications:
      formValues.receivesPendingQuestionsNotifications,
  };
};
